import { useState } from 'react';
import Modal from '../components/Modal.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { fmtCents, parseAmountToCents, fmtDateShort, todayIso } from '../format.js';

export default function Debts({ state, generic, mutate, reload, showToast, onBack }) {
  const [editing, setEditing] = useState(null);
  const visible = state.debts.filter(d => !d.archived).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const total = visible.reduce((s, d) => s + (d.live_balance_cents ?? d.current_cents), 0);
  const totalOriginal = visible.reduce((s, d) => s + d.original_cents, 0);
  const totalPaid = totalOriginal - total;
  const pctPaid = totalOriginal > 0 ? Math.round((totalPaid / totalOriginal) * 1000) / 10 : 0;

  return (
    <div className="screen">
      <div className="row" style={{ marginBottom: 16, gap: 12 }}>
        <button onClick={onBack} className="ghost">‹</button>
        <h1 style={{ margin: 0 }}>Debts</h1>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="muted tiny">Total Outstanding</div>
        <div className="amount" style={{ fontSize: 22, fontWeight: 600 }}>{fmtCents(total)}</div>
        <div className="tiny muted" style={{ marginTop: 4 }}>
          {fmtCents(totalPaid)} paid down · {pctPaid}% of original {fmtCents(totalOriginal)}
        </div>
      </div>

      <div className="row between" style={{ marginBottom: 12 }}>
        <span className="muted tiny">{visible.length} active debts</span>
        <button className="primary" onClick={() => setEditing('new')}>+ New</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {visible.length === 0 ? (
          <div className="empty"><div className="icon">∅</div><div>No debts tracked</div></div>
        ) : (
          visible.map(d => (
            <button key={d.id} onClick={() => setEditing(d)} className="list-item"
                    style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                             borderBottom: '1px solid var(--border2)', borderRadius: 0, cursor: 'pointer' }}>
              <div className="grow">
                <div>
                  {d.name}
                  {d.account_id && <span className="pill info" style={{ marginLeft: 8 }}>Live</span>}
                </div>
                <div className="meta">
                  {(d.apr_basis_points / 100).toFixed(1)}% APR
                  {d.promo_label && ` · ${d.promo_label}`}
                  {d.due_day && ` · due day ${d.due_day}`}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="amount neg">{fmtCents(d.live_balance_cents ?? d.current_cents)}</div>
                {d.account_id && d.live_balance_cents != null && d.live_balance_cents !== d.current_cents && (
                  <div className="tiny muted" style={{ textDecoration: 'line-through' }}>
                    {fmtCents(d.current_cents)}
                  </div>
                )}
                {d.min_payment_cents > 0 && (
                  <div className="tiny muted">min {fmtCents(d.min_payment_cents)}</div>
                )}
              </div>
            </button>
          ))
        )}
      </div>

      <DebtEditor editing={editing} onClose={() => setEditing(null)}
                  state={state} generic={generic} mutate={mutate} reload={reload} showToast={showToast} />
    </div>
  );
}

function DebtEditor({ editing, onClose, state, generic, mutate, reload, showToast }) {
  if (!editing) return null;
  const isNew = editing === 'new';
  const initial = isNew ? {
    name: '', original_cents: 0, current_cents: 0, apr_basis_points: 0,
    promo_label: '', min_payment_cents: 0, due_day: null, payoff_target: '', notes: '',
  } : editing;

  return (
    <Modal open={!!editing} onClose={onClose} title={isNew ? 'New Debt' : 'Edit Debt'}>
      <DebtForm key={isNew ? 'new' : editing.id} initial={initial} isNew={isNew}
                state={state} generic={generic} mutate={mutate} reload={reload} showToast={showToast} onClose={onClose} />
    </Modal>
  );
}

function DebtForm({ initial, isNew, state, generic, mutate, reload, showToast, onClose }) {
  const ask = useConfirm();
  const [form, setForm] = useState({
    name: initial.name ?? '',
    originalInput: initial.original_cents ? (initial.original_cents / 100).toFixed(2) : '',
    currentInput: initial.current_cents ? (initial.current_cents / 100).toFixed(2) : '',
    aprInput: initial.apr_basis_points ? (initial.apr_basis_points / 100).toFixed(2) : '',
    promo_label: initial.promo_label ?? '',
    minPaymentInput: initial.min_payment_cents ? (initial.min_payment_cents / 100).toFixed(2) : '',
    due_day: initial.due_day ?? '',
    payoff_target: initial.payoff_target ?? '',
    notes: initial.notes ?? '',
    account_id: initial.account_id ?? '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.name) {
      showToast({ kind: 'error', message: 'Name required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        original_cents: parseAmountToCents(form.originalInput) ?? 0,
        current_cents: parseAmountToCents(form.currentInput) ?? 0,
        apr_basis_points: Math.round(parseFloat(form.aprInput || '0') * 100) || 0,
        promo_label: form.promo_label || null,
        min_payment_cents: parseAmountToCents(form.minPaymentInput) ?? 0,
        due_day: form.due_day ? parseInt(form.due_day, 10) : null,
        payoff_target: form.payoff_target || null,
        notes: form.notes || '',
        account_id: form.account_id || null,
      };
      if (isNew) await generic.debts.create(payload);
      else await generic.debts.update(initial.id, payload);
      showToast({ kind: 'success', message: 'Saved: ' + payload.name });
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  async function archive() {
    const ok = await ask({ title: 'Archive debt?', message: 'You can restore it later.', confirmLabel: 'Archive', danger: true }); if (!ok) return;
    setSaving(true);
    try {
      await generic.debts.remove(initial.id);
      showToast({ kind: 'success', message: 'Archived.' });
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  // Live balance — only meaningful for linked debts where the worker has
  // computed the running figure. For unlinked debts, "current" IS the live
  // balance (they're the same value — there's no ledger to subtract from).
  const isLinked = !!initial.account_id;
  const liveBalance = initial.live_balance_cents ?? initial.current_cents ?? 0;
  const baselineCents = initial.current_cents ?? 0;
  const paidDownVsBaseline = baselineCents - liveBalance;

  // Quick-adjust modal state
  const [adjustOpen, setAdjustOpen] = useState(false);

  return (
    <>
      {/* ── Live balance summary (linked debts only) ───────────── */}
      {isLinked && !isNew && (
        <div className="card" style={{
          marginBottom: 16, padding: 12,
          background: 'var(--surface2)', border: '1px solid var(--border2)',
        }}>
          <div className="row between" style={{ alignItems: 'baseline' }}>
            <span className="muted tiny">CURRENT LIVE BALANCE</span>
            <span style={{ fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
              {fmtCents(liveBalance)}
            </span>
          </div>
          {paidDownVsBaseline > 0 && (
            <div className="tiny muted" style={{ marginTop: 4 }}>
              {fmtCents(paidDownVsBaseline)} paid down from baseline of {fmtCents(baselineCents)}
            </div>
          )}
          <button
            onClick={() => setAdjustOpen(true)}
            className="primary"
            style={{ marginTop: 10, width: '100%' }}
            disabled={saving}
          >
            Adjust balance (log a charge or payment)
          </button>
          <div className="tiny muted" style={{ marginTop: 6 }}>
            Use this for ad-hoc purchases or one-off payments. It logs a real
            transaction, so the live balance updates and the change shows in Activity.
          </div>
        </div>
      )}

      <div className="field"><label>Name</label>
        <input value={form.name} onChange={(e) => update('name', e.target.value)} autoFocus /></div>
      <div className="field">
        <label>Linked Account (optional)</label>
        <select value={form.account_id} onChange={(e) => update('account_id', e.target.value)}>
          <option value="">— Manual —</option>
          {state.accounts.filter(a => !a.archived).map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <div className="tiny muted" style={{ marginTop: 4 }}>
          When linked, payments to this account auto-reduce the balance shown.
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Original Balance</label>
          <input inputMode="decimal" value={form.originalInput} onChange={(e) => update('originalInput', e.target.value)} />
          <div className="tiny muted" style={{ marginTop: 4 }}>
            What you started with. Used for "% paid off" stats only.
          </div>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>{isLinked ? 'Baseline' : 'Current Balance'}</label>
          <input inputMode="decimal" value={form.currentInput} onChange={(e) => update('currentInput', e.target.value)} />
          <div className="tiny muted" style={{ marginTop: 4 }}>
            {isLinked
              ? 'Anchor for live balance. Don\'t edit unless re-syncing to bank.'
              : 'What you currently owe.'}
          </div>
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}><label>APR %</label>
          <input inputMode="decimal" value={form.aprInput} onChange={(e) => update('aprInput', e.target.value)} placeholder="0.0" /></div>
        <div className="field" style={{ flex: 1 }}><label>Min Payment</label>
          <input inputMode="decimal" value={form.minPaymentInput} onChange={(e) => update('minPaymentInput', e.target.value)} /></div>
        <div className="field" style={{ flex: '0 0 80px' }}><label>Due Day</label>
          <input type="number" min={1} max={31} value={form.due_day} onChange={(e) => update('due_day', e.target.value)} /></div>
      </div>
      <div className="field"><label>Promo Label</label>
        <input value={form.promo_label} onChange={(e) => update('promo_label', e.target.value)} placeholder="e.g. Sept 2026 → 29% deferred" /></div>
      <div className="field"><label>Payoff Target</label>
        <input value={form.payoff_target} onChange={(e) => update('payoff_target', e.target.value)} placeholder="e.g. May 2026" /></div>
      <div className="field"><label>Notes</label>
        <input value={form.notes} onChange={(e) => update('notes', e.target.value)} /></div>
      <div className="row between" style={{ marginTop: 24 }}>
        {!isNew ? <button className="danger" onClick={archive} disabled={saving}>Archive</button> : <span />}
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : 'Save'}</button>
        </div>
      </div>

      {/* ── Quick adjust modal ───────────────────────────────────── */}
      {adjustOpen && (
        <AdjustBalanceModal
          debt={initial}
          state={state}
          mutate={mutate}
          reload={reload}
          showToast={showToast}
          onClose={() => setAdjustOpen(false)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Quick adjust: log a one-off transaction on the linked account.
// "Charge" = a purchase increased what you owe (negative on the CC account).
// "Payment" = an ad-hoc payment outside of recurring rules. We default to the
// simple single-leg case: just record a +cents on the CC account. Users who
// want to cleanly debit Checking too should use Activity → Add → Transfer.
// ─────────────────────────────────────────────────────────────────────────────
function AdjustBalanceModal({ debt, state, mutate, reload, showToast, onClose }) {
  const [direction, setDirection] = useState('charge'); // 'charge' | 'payment'
  const [amountInput, setAmountInput] = useState('');
  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Pick a sensible category default
  const debtPaymentCat = state.categories.find(c => c.name === 'Debt Payment');
  const otherCat       = state.categories.find(c => c.name === 'Other')
                       ?? state.categories.find(c => c.name === 'Discretionary - Flex')
                       ?? state.categories[0];

  async function submit() {
    const cents = parseAmountToCents(amountInput);
    if (!cents || cents <= 0) {
      showToast({ kind: 'error', message: 'Enter a positive amount.' });
      return;
    }
    setSaving(true);
    try {
      // Charge → negative on the CC account (you owe more); category=Other.
      // Payment → positive on the CC account (debt goes down); category=Debt Payment.
      const signed = direction === 'charge' ? -cents : cents;
      const cat = direction === 'charge' ? otherCat : debtPaymentCat;
      const desc = description.trim() || (direction === 'charge'
        ? `Manual charge — ${debt.name}`
        : `Manual payment — ${debt.name}`);

      await mutate.addTransaction({
        occurred_on: date,
        description: desc,
        category_id: cat?.id,
        type: direction === 'charge' ? 'Expense' : 'Debt Payment',
        account_id: debt.account_id,
        amount_cents: signed,
        notes: 'Logged via debt edit modal',
      });

      // Reload to refresh live_balance_cents on the debt
      if (reload) await reload();

      showToast({
        kind: 'success',
        message: direction === 'charge'
          ? `Logged ${fmtCents(cents)} charge to ${debt.name}.`
          : `Logged ${fmtCents(cents)} payment to ${debt.name}.`,
      });
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={true} onClose={onClose} title={`Adjust ${debt.name}`}>
      <div className="muted tiny" style={{ marginBottom: 12 }}>
        Logs a transaction on the {debt.name} account. The live balance will
        update immediately.
      </div>
      <div className="field">
        <label>What kind of adjustment?</label>
        <div className="row" style={{ gap: 8 }}>
          <button
            className={direction === 'charge' ? 'primary' : ''}
            onClick={() => setDirection('charge')}
            style={{ flex: 1 }}
            disabled={saving}
          >
            Charge (+ debt)
          </button>
          <button
            className={direction === 'payment' ? 'primary' : ''}
            onClick={() => setDirection('payment')}
            style={{ flex: 1 }}
            disabled={saving}
          >
            Payment (− debt)
          </button>
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Amount</label>
          <input inputMode="decimal" value={amountInput}
                 onChange={(e) => setAmountInput(e.target.value)}
                 placeholder="0.00" autoFocus />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Date</label>
          <input type="date" value={date}
                 onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label>Description (optional)</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)}
               placeholder={direction === 'charge'
                 ? `e.g. Amazon purchase`
                 : `e.g. Extra principal payment`} />
      </div>
      <div className="row between" style={{ marginTop: 16 }}>
        <button onClick={onClose} disabled={saving}>Cancel</button>
        <button className="primary" onClick={submit} disabled={saving}>
          {saving ? <span className="spinner"/> : 'Log it'}
        </button>
      </div>
    </Modal>
  );
}
